import { useRef, useState, type FormEvent } from 'react';

import { Camera } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { UserRead } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

import {
  photoUrlFor,
  useCreateUser,
  useDepartments,
  useRemovePhoto,
  useRoles,
  useUpdateUser,
  useUploadPhoto,
} from '../api';
import styles from './UserDrawer.module.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

type Errors = {
  full_name?: string | undefined;
  email?: string | undefined;
  photo?: string | undefined;
};

export type UserDrawerProps = {
  open: boolean;
  /** The staff member being edited, or null to create a new one. */
  user: UserRead | null;
  onClose: () => void;
  onCreated: (created: { name: string; token: string; expiresAt: string }) => void;
};

export function UserDrawer({ open, user, onClose, onCreated }: UserDrawerProps) {
  const toast = useToast();
  const editing = user !== null;

  const roles = useRoles();
  const departments = useDepartments();
  const create = useCreateUser();
  const update = useUpdateUser();
  const uploadPhoto = useUploadPhoto();
  const removePhoto = useRemovePhoto();

  // Initialised from the record, not synchronised to it. The parent keys this
  // component on the record's id, so opening a different person remounts it and every
  // field starts fresh: no effect, and no chance of one edit bleeding into the next.
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [roleId, setRoleId] = useState(user?.role.id ?? '');
  const [departmentId, setDepartmentId] = useState(user?.department?.id ?? '');
  const [errors, setErrors] = useState<Errors>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const pickPhoto = (file: File | undefined) => {
    if (!file || !user) return;
    if (file.size > MAX_PHOTO_BYTES) {
      setErrors((current) => ({ ...current, photo: 'The image must be under 2 MB.' }));
      return;
    }
    setErrors((current) => ({ ...current, photo: undefined }));
    uploadPhoto.mutate(
      { id: user.id, file },
      {
        onSuccess: () => {
          toast.success('Photo updated.');
        },
        onError: (error) => {
          setErrors((current) => ({
            ...current,
            photo: error instanceof ApiError ? error.message : 'That upload failed.',
          }));
        },
      },
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const next: Errors = {};
    if (!fullName.trim()) next.full_name = "Enter the staff member's full name.";
    if (!email.trim()) next.email = 'Enter a work email address.';
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = 'Enter a valid email address.';
    setErrors((current) => ({ ...current, ...next }));
    if (Object.keys(next).length > 0) return;

    const payload = {
      full_name: fullName.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      department_id: departmentId || null,
      ...(roleId ? { role_id: roleId } : {}),
    };

    if (editing) {
      update.mutate(
        { id: user.id, ...payload },
        {
          onSuccess: () => {
            toast.success(`${fullName.trim()} updated.`);
            onClose();
          },
        },
      );
      return;
    }

    create.mutate(payload, {
      onSuccess: (result) => {
        onCreated({
          name: result.user.full_name,
          token: result.invite.token,
          expiresAt: result.invite.expires_at,
        });
        onClose();
      },
    });
  };

  const mutation = editing ? update : create;
  const error = mutation.error;

  /**
   * A field-level message from the server, mapped onto its input.
   *
   * The client checks the obvious things, but the server is the authority and its
   * messages have to land on the right field rather than in a banner nobody connects
   * to anything.
   */
  const serverFieldError = (field: string): string | undefined =>
    error instanceof ApiError
      ? error.fieldErrors.find((entry) => entry.field === field)?.message
      : undefined;

  /** A conflict is about the record as a whole, so it belongs on the email field. */
  const emailConflict =
    error instanceof ApiError && error.code === 'EMAIL_IN_USE' ? error.message : undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={editing ? 'Edit staff member' : 'Add staff member'}
      description={
        editing
          ? "Update this person's details and access."
          : 'Create the account and get an invitation link to send them.'
      }
      lead={
        editing ? <Avatar name={fullName || '?'} src={photoUrlFor(user)} size={42} /> : null
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="user-form" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Add staff member'}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={submit} noValidate>
        {error instanceof ApiError && !emailConflict && !error.isValidation ? (
          <Alert tone="danger" className={styles.banner}>
            {error.message}
          </Alert>
        ) : null}

        {editing ? (
          <div className={styles.photo}>
            <Avatar name={fullName || '?'} src={photoUrlFor(user)} size={64} />
            <div className={styles.photoActions}>
              <div className={styles.photoButtons}>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Camera size={14} />}
                  loading={uploadPhoto.isPending}
                  onClick={() => fileRef.current?.click()}
                >
                  {user.photo_path ? 'Replace photo' : 'Upload photo'}
                </Button>
                {user.photo_path ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={removePhoto.isPending}
                    onClick={() => {
                      removePhoto.mutate(user.id);
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
              <p className={styles.photoHint}>JPEG, PNG or WebP, up to 2 MB.</p>
              {errors.photo ? (
                <p className={styles.photoHint} role="alert" style={{ color: 'var(--danger)' }}>
                  {errors.photo}
                </p>
              ) : null}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(event) => {
                pickPhoto(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </div>
        ) : null}

        <p className={styles.sectionTitle}>Personal details</p>

        <Field
          label="Full name"
          required
          error={errors.full_name ?? serverFieldError('full_name')}
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              placeholder="e.g. Priya Nair"
              value={fullName}
              onChange={(event) => {
                setFullName(event.target.value);
                setErrors((current) => ({ ...current, full_name: undefined }));
              }}
            />
          )}
        </Field>

        <div className={styles.twoUp}>
          <Field
            label="Work email"
            required
            error={errors.email ?? emailConflict ?? serverFieldError('email')}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                type="email"
                placeholder="name@flyst.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setErrors((current) => ({ ...current, email: undefined }));
                }}
              />
            )}
          </Field>

          <Field label="Phone">
            {({ id }) => (
              <Input
                id={id}
                placeholder="+91 98200 00000"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                }}
              />
            )}
          </Field>
        </div>

        <p className={styles.sectionTitle}>Access</p>

        <div className={styles.twoUp}>
          <Field
            label="Role"
            hint={editing ? undefined : 'Defaults to Staff, least privilege by default.'}
          >
            {({ id }) => (
              <Select
                id={id}
                value={roleId}
                placeholder="Staff"
                options={(roles.data ?? []).map((role) => ({
                  value: role.id,
                  label: role.name,
                }))}
                onChange={(event) => {
                  setRoleId(event.target.value);
                }}
              />
            )}
          </Field>

          <Field label="Department">
            {({ id }) => (
              <Select
                id={id}
                value={departmentId}
                placeholder="None"
                options={(departments.data ?? []).map((department) => ({
                  value: department.id,
                  label: department.name,
                }))}
                onChange={(event) => {
                  setDepartmentId(event.target.value);
                }}
              />
            )}
          </Field>
        </div>

        {editing ? null : (
          <Alert tone="info">
            The account is created as invited. You will get a single-use link to send them, and
            they choose their own password.
          </Alert>
        )}
      </form>
    </Drawer>
  );
}
